import type { Language } from '@/admin/languages';
import type { components } from '@/api/qraft/schema';

export type CannedNode = components['schemas']['CannedNode'];

export const TEMPLATE_TABS = [
  'emails',
  'notifications',
  'activity',
  'canned',
] as const;
export type TemplateTab = (typeof TEMPLATE_TABS)[number];

export const CANNED_NODES: CannedNode[] = [
  'student-encouragement',
  'student-introduction',
  'quiz-question-feedback',
  'quiz-final-thoughts',
];

const LANGUAGES: Language[] = ['EN', 'ES', 'FR', 'PT_BR'];

export type YesNo = 'yes' | 'no';

export type TemplatesSearch = {
  tab: TemplateTab;
  /** What the open editor edits: an email type, preference, activity type or canned id (or "new"). */
  item?: string;
  lang?: Language;
  /** 1-based in the URL, like every other admin list. */
  page?: number;
  q?: string;
  /** Emails: only types a sender uses (yes) or none does (no). */
  sender?: YesNo;
  /** Canned defaults filters. */
  builtin?: YesNo;
  node?: CannedNode;
  object?: number;
  owner?: number;
};

function oneOf<T extends string>(raw: unknown, allowed: readonly T[]) {
  return typeof raw === 'string' && (allowed as readonly string[]).includes(raw)
    ? (raw as T)
    : undefined;
}

function text(raw: unknown): string | undefined {
  if (typeof raw === 'number') return String(raw);
  return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
}

function whole(raw: unknown, min: number): number | undefined {
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw)
        : Number.NaN;
  return Number.isInteger(n) && n >= min && n <= 2147483647 ? n : undefined;
}

/** A bad or missing value falls back to its default rather than throwing the route. */
export function parseTemplatesSearch(
  search: Record<string, unknown>,
): TemplatesSearch {
  return {
    tab: oneOf(search.tab, TEMPLATE_TABS) ?? 'emails',
    item: text(search.item),
    lang: oneOf(search.lang, LANGUAGES),
    page: whole(search.page, 2),
    q: text(search.q),
    sender: oneOf(search.sender, ['yes', 'no'] as const),
    builtin: oneOf(search.builtin, ['yes', 'no'] as const),
    node: oneOf(search.node, CANNED_NODES),
    object: whole(search.object, 0),
    owner: whole(search.owner, 1),
  };
}

export const yesNoToBool = (v: YesNo | undefined) =>
  v === undefined ? undefined : v === 'yes';

/** Zero-based page for the Pagination control; the URL carries it 1-based. */
export const pageIndex = (page: number | undefined) => (page ?? 1) - 1;

/**
 * Inserts `token` where the caret was, replacing any selection, and returns the
 * new value plus where the caret belongs afterwards.
 */
export function insertAtCaret(
  value: string,
  token: string,
  start: number | null,
  end: number | null,
): { value: string; caret: number } {
  const from = start ?? value.length;
  const to = end ?? from;
  return {
    value: value.slice(0, from) + token + value.slice(to),
    caret: from + token.length,
  };
}
